import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAllProducts } from "../../services/productService";
import { createDelivery } from "../../services/deliveryService";
import toast from "react-hot-toast";

// Utility to get today's date in YYYY-MM-DD format
const getTodayDate = () => new Date().toISOString().split("T")[0];

const CreateDelivery = ({ customer }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    delivery_date: getTodayDate(),
    products: {}, // Stores product-wise quantities (Delivered & Returned)
  });

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productData = await getAllProducts();
        setProducts(productData);

        // ✅ Initialize product quantities (Delivered & Returned)
        const initialQuantities = {};
        productData.forEach((product) => {
          initialQuantities[product.id] = { quantity: 0, returned: 0 };
        });
        setFormData((prev) => ({ ...prev, products: initialQuantities }));
      } catch (error) {
        toast.error("Failed to fetch products.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // ✅ Handle Increment/Decrement
  const handleQuantityChange = (productId, type, action) => {
    setFormData((prev) => ({
      ...prev,
      products: {
        ...prev.products,
        [productId]: {
          ...prev.products[productId],
          [type]:
            action === "increment"
              ? prev.products[productId][type] + 1
              : Math.max(prev.products[productId][type] - 1, 0),
        },
      },
    }));
  };

  // ✅ Submit Delivery Data in JSON Format
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true); // Set loading state

      const deliveryPayload = {
        customer_id: customer.id,
        delivery_date: formData.delivery_date,
        delivery_status: "Delivered",
        deliveries: Object.keys(formData.products).map((productId) => {
          const product = formData.products[productId];

          return {
            product_id: parseInt(productId),
            quantity_delivered: product.quantity || 0, // ✅ Delivered Quantity
            cool_bottles_returned: productId == 1 ? product.returned : 0, // ✅ Cool (20L Can) Return
            pet_bottles_returned: productId == 2 ? product.returned : 0, // ✅ Pet (20L Bottle) Return
          };
        }),
      };

      // ✅ Check if all values are 0
      const isAllZero = deliveryPayload.deliveries.every(
        ({ quantity_delivered, cool_bottles_returned, pet_bottles_returned }) =>
          quantity_delivered === 0 &&
          cool_bottles_returned === 0 &&
          pet_bottles_returned === 0
      );

      if (isAllZero) {
        toast.error(
          "🚨 Please enter at least one non-zero value for delivery or return."
        );
        setIsSubmitting(false);
        return;
      }

      console.log(
        "🚀 Sending Payload:",
        JSON.stringify(deliveryPayload, null, 2)
      );

      await createDelivery(deliveryPayload);
      toast.success("Delivery created successfully!");

      // ✅ Reset form state
      const resetQuantities = {};
      products.forEach((product) => {
        resetQuantities[product.id] = { quantity: 0, returned: 0 };
      });

      setFormData({
        delivery_date: getTodayDate(), // Reset date to today
        products: resetQuantities, // Reset product quantities
      });

      // ✅ Navigate back after submission
      setTimeout(() => navigate(-1), 1500); // Navigate after 1 sec
    } catch (error) {
      console.error("❌ Delivery Creation Error:", error);
      toast.error("❌ Failed to create delivery.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto bg-white shadow-md rounded-lg overflow-hidden mt-5">
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between bg-blue-200 p-3 rounded-lg">
          {/* Cool Bottles Balance */}
          <div className="flex items-center">
            <span className="bg-white p-2 rounded mr-2">🛢️</span>
            <p className="font-semibold">
              Cool:{" "}
              <span className="text-blue-700">
                {customer.cool_container_balance || "0"}
              </span>
            </p>
          </div>

          {/* Pet Bottles Balance */}
          <div className="flex items-center">
            <span className="bg-white p-2 rounded mr-2">🍼</span>
            <p className="font-semibold">
              Pet:{" "}
              <span className="text-blue-700">
                {customer.pet_container_balance || "0"}
              </span>
            </p>
          </div>
        </div>

        {/* Delivery Date */}
        <div className="mt-3">
          <p className="text-gray-700">Select Date</p>
          <input
            type="date"
            value={formData.delivery_date}
            onChange={(e) =>
              setFormData({ ...formData, delivery_date: e.target.value })
            }
            className="w-full p-2 border rounded-lg"
          />
        </div>

        {/* Product List (Dynamic UI) */}
        {products.map((product) => (
          <div key={product.id} className="mt-3 bg-red-200 p-3 rounded-lg">
            <p className="font-semibold">
              {product.product_name}{" "}
              <span className="text-gray-600">
                (In-{formData.products[product.id]?.quantity} | Out-
                {formData.products[product.id]?.returned})
              </span>
            </p>

            {/* Delivered Quantity (In) */}
            <div className="flex items-center justify-between mt-2">
              <p>🛢️ Full (in)</p>
              <div className="flex items-center">
                <button
                  className="bg-blue-500 text-white px-2"
                  onClick={() =>
                    handleQuantityChange(product.id, "quantity", "decrement")
                  }
                >
                  -
                </button>
                <span className="mx-2">
                  {formData.products[product.id]?.quantity}
                </span>
                <button
                  className="bg-blue-500 text-white px-2"
                  onClick={() =>
                    handleQuantityChange(product.id, "quantity", "increment")
                  }
                >
                  +
                </button>
              </div>
            </div>

            {/* Returned Quantity (Out) */}
            <div className="flex items-center justify-between mt-1">
              <p>🛢️ Empty (Out)</p>
              <div className="flex items-center">
                <button
                  className="bg-blue-500 text-white px-2"
                  onClick={() =>
                    handleQuantityChange(product.id, "returned", "decrement")
                  }
                >
                  -
                </button>
                <span className="mx-2">
                  {formData.products[product.id]?.returned}
                </span>
                <button
                  className="bg-blue-500 text-white px-2"
                  onClick={() =>
                    handleQuantityChange(product.id, "returned", "increment")
                  }
                >
                  +
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* ✅ Submit Button with Loader */}
        <button
          className="w-full mt-3 bg-blue-700 text-white p-3 rounded-lg flex justify-center items-center"
          onClick={handleSubmit}
          disabled={isSubmitting} // ✅ Disable button when submitting
        >
          {isSubmitting ? (
            <span className="animate-spin border-t-2 border-white border-solid h-5 w-5 rounded-full"></span>
          ) : (
            "Submit"
          )}
        </button>
      </div>
    </div>
  );
};

export default CreateDelivery;
